import { graphColors } from "../entity/chart/chart-service";
import { patchData } from "../api/api-tools";

export const REBA_FIELDS = [
  'step_1', // nuque
  'step_2', // tronc
  'step_3', // jambes
  'step_4', // score table A
  'step_7', // epaule
  'step_8', // coude
  'step_9', // poignet
  'step_10', // score table B
  'step_13', // score table C
];

const REBA_THRESHOLDS = {
  'step_1': [1, 2, 3], // nuque
  'step_2': [1, 3, 4], // tronc
  'step_3': [1, 2, 3], // jambes
  'step_4': [1, 5, 8], // score table A
  'step_7': [1, 3, 4], // epaule
  'step_8': [1, 2, 1000], // coude
  'step_9': [1, 2, 3], // poignet
  'step_10': [1, 4, 7], // score table B
  'step_13': [1, 2, 4, 7, 9], // score table C
};

export const REBA_THRESHOLDS_ADJUSTED = Object.fromEntries(
  Object.entries(REBA_THRESHOLDS).map(([k,v]) => [k, v.map((n,i) => i===0 ? n : (v[i-1]+n)/2)])
);

export const REBA_THRESHOLDS_INFLUXDB = Object.entries(REBA_THRESHOLDS_ADJUSTED).map(([key, values]) => ({
  _field: key,
  threshold: values[Math.floor(values.length / 2)] ?? 0.0,
}));

export const VIEW_MODE_GLOBAL = 0;
export const VIEW_MODE_DETAILS = 1;

const now = new Date();
const nextHour = new Date(now);
nextHour.setMinutes(0);
nextHour.setSeconds(0);
nextHour.setMilliseconds(0);
nextHour.setHours(nextHour.getHours() + 1);
const today = new Date(nextHour);
const yesterday = new Date(nextHour);
yesterday.setDate(yesterday.getDate() - 1);

export const DEFAULT_DATA_RANGE = {
  id: 1,
  start: yesterday.getTime(),
  stop: today.getTime(),
  window: '15m',
  window_s: 900,
  unit: 'minute',
  stepSize: 60,
};

export const LIVE_DATA_RANGE = {
  id: 1,
  start: now.getTime() - 5 * 60 * 1000,
  stop: now.getTime(),
  window: '10s',
  window_s: 10,
  unit: 'second',
  stepSize: 15,
};

export const createDateRange = (startTimestamp, stopTimestamp) => {
  const durationSec = (stopTimestamp - startTimestamp) / 1000;
  let window, window_s, unit, stepSize;
  if (durationSec <= 5 * 60) // 300s
    window = '1s', window_s = 1, unit = 'second', stepSize = 2; // each 2 s
  else if (durationSec <= 15 * 60) // 900s
    window = '3s', window_s = 3, unit = 'minute', stepSize = 1; // each 1 min
  else if (durationSec <= 30 * 60) // 30min
    window = '10s', window_s = 10, unit = 'minute', stepSize = 3; // each 3 min
  else if (durationSec <= 2 * 60 * 60) // 2h
    window = '2m', window_s = 120, unit = 'minute', stepSize = 10; // each 10 min
  else if (durationSec <= 6 * 60 * 60) // 6h
    window = '5m', window_s = 300, unit = 'minute', stepSize = 30; // each 30 min
  else if (durationSec <= 24 * 60 * 60) // 24h
    window = '15m', window_s = 900, unit = 'minute', stepSize = 60; // each 1 h
  else if (durationSec <= 3 * 24 * 60 * 60) // 3d
    window = '1h', window_s = 3600, unit = 'hour', stepSize = 12; // each 12 h
  else if (durationSec <= 7 * 24 * 60 * 60) // 1w
    window = '3h', window_s = 10800, unit = 'hour', stepSize = 24; // each 24 h
  else if (durationSec <= 30 * 24 * 60 * 60) // 1month
    window = '1d', window_s = 86400, unit = 'day', stepSize = 2; // each 2 d
  else
    window = '1w', window_s = 604800, unit = 'week', stepSize = 1; // each 1 w

  return {
    start: startTimestamp,
    stop: stopTimestamp,
    window: window,
    window_s: window_s,
    unit: unit,
    stepSize: stepSize,
  };
};

export const createPhases = (seriesData, seriesNames) => {
  const phasesData = buildPhases1s(seriesData, seriesNames);

  return Object.fromEntries(
    phasesData.flatMap(p => {
      const mid = (p.start + p.end) / 2;

      const box = [p.id, {
        type: 'box',
        xScaleID: 'x',
        yScaleID: 'y',
        xMin: p.start,
        xMax: p.end,
        yMin: 'min',
        yMax: 'max',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderColor: p.border ?? 'rgba(255,255,255,0.5)',
        borderWidth: 0,
        drawTime: 'beforeDatasetsDraw',
      }];

      const label = [`${p.id}_label`, {
        type: 'label',
        xScaleID: 'x',
        yScaleID: 'y',
        xValue: () => (p.start + p.end) / 2,
        yValue: (ctx) => ctx.chart.scales.y?.max,
        position: { x: 'center', y: 'start' },
        content: () => p.label,
        backgroundColor: 'rgba(0,0,0,0.35)',
        color: '#ddd',
        padding: 4,
        borderRadius: 4,
        font: { weight: '600' },
        drawTime: 'afterDatasetsDraw',
        clip: { x: true, y: true },
        display: (ctx) => {
          const chart = ctx?.chart;
          const ca = chart?.chartArea;
          const xScale = chart?.scales?.x;
          if (!chart || !ca || !xScale) return false;
          if (!Number.isFinite(xScale.min) || !Number.isFinite(xScale.max)) return false;

          const text = p.label || '';
          // mesurer le texte seulement quand le contexte est prêt
          const padX = 12; // padding horizontal total
          const w = chart.ctx?.measureText ? chart.ctx.measureText(text).width + padX : 0;

          const xStart = xScale.getPixelForValue(p.start);
          const xEnd   = xScale.getPixelForValue(p.end);
          if (!Number.isFinite(xStart) || !Number.isFinite(xEnd)) return false;

          const visLeft  = Math.max(ca.left, Math.min(xStart, xEnd));
          const visRight = Math.min(ca.right, Math.max(xStart, xEnd));
          const visWidth = visRight - visLeft;

          // n'affiche rien si le label ne tient pas entièrement dans la box visible
          return visWidth > 0 && w <= visWidth;
        },
      }];

      return [box, label];
    })
  );
};

export const buildPhases1s = (seriesDict, labels) => {
  const phases = [];

  for (const seriesName of Object.keys(seriesDict)) {
    if (seriesName.startsWith("step_")) continue;

    const series = seriesDict[seriesName];
    let inPhase = false;
    let startTime = null;

    for (let i = 0; i < series.length; i++) {
      const { time, value } = series[i];

      if (!inPhase && value > 0) {
        inPhase = true;
        startTime = time;
      } else if (inPhase && value <= 0) {
        const endExclusive = time;
        phases.push({
          id: `phase_${phases.length + 1}`,
          start: new Date(startTime).getTime(),
          end: new Date(endExclusive).getTime(),
          label: labels[seriesName],
        });
        inPhase = false;
        startTime = null;
      }
    }

    // Fin de série encore "en phase"
    if (inPhase && startTime != null) {
      const lastTime = series[series.length - 1].time;
      const endPlus1s = new Date(new Date(lastTime).getTime() + 1000).toISOString();
      phases.push({
        id: `phase_${phases.length + 1}`,
        start: new Date(startTime).getTime(),
        end: new Date(endPlus1s).getTime(),
        label: labels[seriesName],
      });
    }
  }

  return phases;
};

export const convertPhasesToTimelineData = (phases, labels) => {
  const labelToGroupId = {};
  const groups = labels.map((label, index) => {
    const id = index + 1;
    labelToGroupId[label] = id;
    return { id, title: label };
  });

  const items = phases.map((phase) => {
    const groupId = labelToGroupId[phase.label];
    const colorIndex = (groupId - 1) % graphColors.length;
    return {
      id: phase.id,
      group: groupId,
      title: phase.label,
      start_time: phase.start,
      end_time: phase.end,
      color: graphColors[colorIndex]
    };
  });

  return { groups, items };
};

export const saveLayout = (layout) => {
  localStorage.setItem('ergonomics_layout', JSON.stringify(layout));
};

export const loadLayout = () => {
  const saved = localStorage.getItem('ergonomics_layout');
  return saved ? JSON.parse(saved) : null;
};

export const saveLayoutData = async (layoutData, saveToDatabase=true) => {
  localStorage.setItem('ergonomics_layout_data', JSON.stringify(layoutData));

  if (saveToDatabase && layoutData?.id) {
    const viewBody = {
      layout_data: layoutData,
    }
    const response = await patchData(`ergonomics_views/${layoutData.id}`, viewBody);
  }
};

export const loadLayoutData = () => {
  const saved = localStorage.getItem('ergonomics_layout_data');
  return saved ? JSON.parse(saved) : null;
};